"use client";
import { useReducer, useState, Suspense } from "react";
import { OtherGames } from "@/components/ui/games/OtherGames";
import { PERSONALITY_QUESTIONS, PERSONALITY_TYPES, WORKFLOW_ADJECTIVES, Question } from "./constants";

type QuizState =
  | { phase: "name-entry"; playerName: string; error?: string }
  | { phase: "quiz"; playerName: string; currentQuestion: number; answers: string[] }
  | { phase: "results"; playerName: string; answers: string[]; personalityType: string };

type QuizAction =
  | { type: "UPDATE_NAME"; payload: string }
  | { type: "SET_ERROR"; payload: string }
  | { type: "START_GAME" }
  | { type: "NEXT_QUESTION"; payload: string }
  | { type: "PREVIOUS_QUESTION" }
  | { type: "RESTART_QUIZ" };

const calculatePersonalityType = (answers: string[]) => {
  // Exclude the last answer which is workflow preference (W, X, Y, Z)
  const personalityAnswers = answers.slice(0, -1);
  const counts = { A: 0, B: 0, C: 0, D: 0, E: 0 };
  personalityAnswers.forEach((answer) => {
    if (answer in counts) {
      counts[answer as keyof typeof counts]++;
    }
  });

  const maxCount = Math.max(...Object.values(counts));
  const dominantType = Object.keys(counts).find((key) => counts[key as keyof typeof counts] === maxCount);

  return dominantType as keyof typeof PERSONALITY_TYPES;
};

function quizReducer(state: QuizState, action: QuizAction): QuizState {
  switch (action.type) {
    case "UPDATE_NAME":
      if (state.phase !== "name-entry") return state;
      return { ...state, playerName: action.payload, error: undefined };

    case "SET_ERROR":
      if (state.phase !== "name-entry") return state;
      return { ...state, error: action.payload };

    case "START_GAME":
      if (state.phase !== "name-entry") return state;
      if (!state.playerName.trim()) {
        return { ...state, error: "Please enter your name" };
      }
      if (state.playerName.trim().length < 2) {
        return { ...state, error: "Name must be at least 2 characters" };
      }
      return {
        phase: "quiz",
        playerName: state.playerName.trim(),
        currentQuestion: 0,
        answers: [],
      };

    case "NEXT_QUESTION":
      if (state.phase !== "quiz") return state;
      const newAnswers = [...state.answers, action.payload];
      const nextQuestion = state.currentQuestion + 1;

      if (nextQuestion >= PERSONALITY_QUESTIONS.length) {
        return {
          phase: "results",
          playerName: state.playerName,
          answers: newAnswers,
          personalityType: calculatePersonalityType(newAnswers),
        };
      }

      return {
        ...state,
        currentQuestion: nextQuestion,
        answers: newAnswers,
      };

    case "PREVIOUS_QUESTION":
      if (state.phase !== "quiz" || state.currentQuestion <= 0) return state;
      return {
        ...state,
        currentQuestion: state.currentQuestion - 1,
        answers: state.answers.slice(0, -1),
      };

    case "RESTART_QUIZ":
      return { phase: "name-entry", playerName: "", error: undefined };

    default:
      return state;
  }
}

function PersonalityQuizPageContent() {
  const [state, dispatch] = useReducer(quizReducer, {
    phase: "name-entry",
    playerName: "",
    error: undefined,
  });
  const [shareMessage, setShareMessage] = useState<string>("");

  const handleShare = async () => {
    if (state.phase !== "results") return;

    const result = PERSONALITY_TYPES[state.personalityType as keyof typeof PERSONALITY_TYPES];
    const shareText = result.copyPasta;

    try {
      await navigator.clipboard.writeText(shareText);
      setShareMessage("Results copied to clipboard!");
      setTimeout(() => setShareMessage(""), 3000);
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
      setShareMessage("Failed to copy to clipboard");
      setTimeout(() => setShareMessage(""), 3000);
    }
  };

  if (state.phase === "name-entry") {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 pt-20">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-black">AI Personality Test</h1>
          {/* <p className="text-lg text-gray-600 mb-2">Everyone's got a different relationship with AI. Some people are knee-deep in code, some only open it when they need help writing an email, and plenty are just curious about what's possible.</p> */}
          <p className="text-base text-gray-500 mb-4">
            This quiz is a quick way to see which "AI personality" feels most like you and to get a few tool
            recommendations that could save you time.
          </p>
        </div>

        <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4">
          <div className="space-y-6">
            <input
              type="text"
              placeholder="Enter your name"
              value={state.playerName}
              onChange={(e) => dispatch({ type: "UPDATE_NAME", payload: e.target.value })}
              className="w-full px-6 py-4 text-base border-2 border-indigo-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
              maxLength={20}
              onKeyDown={(e) => e.key === "Enter" && dispatch({ type: "START_GAME" })}
              autoFocus
            />

            {state.error && <div className="text-red-600 text-center font-medium">{state.error}</div>}

            <button
              onClick={() => dispatch({ type: "START_GAME" })}
              className="w-full bg-indigo-100 hover:bg-indigo-200 text-indigo-700 py-3 text-base rounded-xl font-bold transition-all transform hover:scale-105 shadow-sm"
            >
              Start Playing →
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show quiz questions
  if (state.phase === "quiz") {
    const currentQuestion = PERSONALITY_QUESTIONS[state.currentQuestion];
    const shuffledOptions = [...currentQuestion.options].sort(() => 0.5 - Math.random());
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 pt-20">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-black mb-2">
            Question {state.currentQuestion + 1} of {PERSONALITY_QUESTIONS.length}
          </h1>
          <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
            <div
              className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((state.currentQuestion + 1) / PERSONALITY_QUESTIONS.length) * 100}%` }}
            />
          </div>
        </div>

        <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-8 max-w-2xl w-full mx-4">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 text-center">{currentQuestion.question}</h2>

          <div className="space-y-3">
            {shuffledOptions.map((option, index) => (
              <button
                key={index}
                onClick={() => dispatch({ type: "NEXT_QUESTION", payload: option.value })}
                className="w-full p-4 text-left border-2 border-gray-200 rounded-xl hover:border-indigo-300 hover:bg-indigo-50 transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <span className="font-medium text-gray-900">{option.text}</span>
              </button>
            ))}
          </div>

          {state.currentQuestion > 0 && (
            <button
              onClick={() => dispatch({ type: "PREVIOUS_QUESTION" })}
              className="mt-6 px-4 py-2 text-indigo-600 hover:text-indigo-800 font-medium transition-colors"
            >
              ← Previous Question
            </button>
          )}
        </div>
      </div>
    );
  }

  // Show results (state.phase === 'results')
  const result = PERSONALITY_TYPES[state.personalityType as keyof typeof PERSONALITY_TYPES];

  // Get the workflow preference from the last answer
  const workflowPreference = state.answers[state.answers.length - 1];
  const workflowData = WORKFLOW_ADJECTIVES[workflowPreference as keyof typeof WORKFLOW_ADJECTIVES];

  // Filter tools based on workflow preference
  const filteredTools = result.suggestedTools.filter((tool) => {
    // If no workflows defined, include the tool
    if (!tool.workflows || tool.workflows.length === 0) return true;
    // Include tools that match the user's workflow preference
    return tool.workflows.includes(workflowPreference);
  });

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 pt-20">
      <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-8 max-w-2xl w-full mx-4">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-black mb-2">{state.playerName}'s results are in...</h1>
        </div>

        <div className="bg-linear-to-r from-indigo-50 to-purple-50 rounded-2xl p-8 mb-8">
          <div className="text-center">
            <div className="text-6xl mb-4">{result.emoji}</div>
            <h2 className="text-2xl font-bold text-indigo-900 mb-4">
              The {workflowData?.adjective} {result.title}
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">{result.description}</p>
          </div>
        </div>

        <div className="bg-linear-to-r from-indigo-50 to-purple-50 rounded-2xl p-8 mb-8">
          <div className="text-center mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Recommended Tools for You</h3>
            <p className="text-sm text-gray-600 italic">
              As a {workflowData?.adjective?.toLowerCase()} personality, {workflowData?.description?.toLowerCase()}
              These tools were selected to match that preference!
            </p>
          </div>
          <ul className="space-y-2">
            {(filteredTools.length > 0 ? filteredTools : result.suggestedTools).map((tool, index) => (
              <li key={index} className="flex items-start">
                <span className="text-indigo-500 mr-2 mt-1">•</span>
                <span className="text-gray-700">
                  <a
                    href={tool.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-indigo-600 hover:text-indigo-800 underline font-medium"
                  >
                    {tool.name}
                  </a>
                  {" – " + tool.description}
                </span>
              </li>
            ))}
          </ul>
        </div>

        <div className="space-y-4 mb-8">
          <div className="flex gap-3 justify-center">
            <button
              onClick={handleShare}
              className="bg-purple-100 hover:bg-purple-200 text-purple-700 py-3 px-6 rounded-xl font-semibold text-base transition-all transform hover:scale-105 shadow-sm"
            >
              Share Results
            </button>
            <button
              onClick={() => dispatch({ type: "RESTART_QUIZ" })}
              className="bg-indigo-100 hover:bg-indigo-200 text-indigo-700 py-3 px-6 rounded-xl font-semibold text-base transition-all transform hover:scale-105 shadow-sm"
            >
              Take Quiz Again
            </button>
          </div>
          {shareMessage && <div className="mt-3 text-green-600 font-medium text-sm text-center">{shareMessage}</div>}
        </div>
        <OtherGames currentGameId="personality-quiz" />
      </div>
    </div>
  );
}

export default function PersonalityQuizPage() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <PersonalityQuizPageContent />
    </Suspense>
  );
}
